import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  Eye, EyeOff, ArrowLeft, Film, Clapperboard, Globe,
  CheckCircle2, AlertCircle,
} from 'lucide-react';
import kinoraLogo from '../assets/kinora_logo.svg';

// ─── Brand Panel (left, desktop only) ────────────────────────────────────────

const features = [
  { icon: Film, text: 'Launch slice-based crowdfunding campaigns' },
  { icon: Clapperboard, text: 'Premiere your work at exclusive theaters' },
  { icon: Globe, text: 'Distribute content to global audiences' },
];

const BrandPanel: React.FC = () => (
  <div className="hidden lg:flex flex-col justify-between relative overflow-hidden bg-black">
    {/* Gradient layers */}
    <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-purple-950/50 to-slate-950" />
    <div className="absolute inset-0 opacity-[0.07]" style={{
      backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 8px, rgba(255,255,255,1) 8px, rgba(255,255,255,1) 10px)`,
    }} />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/15 rounded-full blur-[140px] pointer-events-none" />

    <div className="relative z-10 flex flex-col justify-center h-full p-12">
      <img
        src={kinoraLogo}
        alt="Kinora"
        className="absolute top-8 left-8 h-12 w-auto select-none"
        draggable={false}
      />

      {/* Center copy */}
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <h2 className="text-4xl font-bold text-white leading-tight">
            Fund the Future<br />of Entertainment
          </h2>
          <p className="text-white/55 text-base leading-relaxed max-w-xs">
            Join thousands of creators, theaters, and distributors on the platform changing how content gets made.
          </p>
        </div>

        <div className="flex flex-col gap-3.5">
          {features.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5 border border-white/10">
                <Icon className="w-4 h-4 text-purple-400" />
              </div>
              <span className="text-white/65 text-sm">{text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ─── Form Field ───────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  autoComplete?: string;
  showToggle?: boolean;
  showPassword?: boolean;
  onToggle?: () => void;
}

const Field: React.FC<FieldProps> = ({
  label, type, value, onChange, autoComplete, showToggle, showPassword, onToggle,
}) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-medium text-foreground">{label}</label>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        className={`w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 transition-all duration-200 ${showToggle ? 'pr-10' : ''}`}
      />
      {showToggle && (
        <button
          type="button"
          onClick={onToggle}
          tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      )}
    </div>
  </div>
);

// ─── Auth Page ────────────────────────────────────────────────────────────────

export default function Auth() {
  const { initializing, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(searchParams.get('mode') === 'signup');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const from = location.state?.from?.pathname || '/select-partner-type';

  useEffect(() => {
    if (!initializing && isAuthenticated()) navigate(from, { replace: true });
  }, [initializing, isAuthenticated, navigate, from]);

  useEffect(() => { setError(''); setSuccessMessage(''); }, [isSignUp]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');
    try {
      if (!isSignUp) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) { setError(error.message); return; }
        navigate('/', { replace: true });
      } else {
        const { data, error } = await supabase.auth.signUp({
          email, password,
          options: { data: { full_name: name } },
        });
        if (error) { setError(error.message); return; }
        if (data.session) {
          navigate('/select-partner-type', { replace: true });
        } else {
          setSuccessMessage('Check your email for a confirmation link, then sign in.');
          toggleMode();
        }
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) { setError('Enter your email address first.'); return; }
    setIsSubmitting(true);
    setError('');
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) setError(error.message);
      else setSuccessMessage('Password reset email sent — check your inbox.');
    } catch {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp(prev => !prev);
    setEmail(''); setPassword(''); setName('');
    setShowPassword(false); setError(''); setSuccessMessage('');
  };

  if (initializing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background grid lg:grid-cols-2">
      <BrandPanel />

      {/* ── Form side ───────────────────────────────────────────── */}
      <div className="flex flex-col items-center justify-center p-6 sm:p-12 relative min-h-screen">

        {/* Back to home */}
        <Link
          to="/"
          className="absolute top-6 left-6 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
          Back to home
        </Link>

        {/* Mobile logo */}
        <img
          src={kinoraLogo}
          alt="Kinora"
          className="lg:hidden h-7 w-auto select-none mb-10"
          draggable={false}
        />

        <div className="w-full max-w-sm">
          {/* Header */}
          <div className="mb-7">
            <h1 className="text-2xl font-bold text-foreground mb-1.5">
              {isSignUp ? 'Create your account' : 'Welcome back'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isSignUp
                ? 'Start building your audience on Kinora'
                : 'Sign in to your studio portal'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {isSignUp && (
              <Field
                label="Full Name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
            )}

            <Field
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <Field
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              showToggle
              showPassword={showPassword}
              onToggle={() => setShowPassword(!showPassword)}
            />

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2.5 rounded-lg bg-destructive/8 border border-destructive/20 px-3 py-2.5">
                <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-sm text-destructive leading-snug">{error}</p>
              </div>
            )}

            {/* Success */}
            {successMessage && (
              <div className="flex items-start gap-2.5 rounded-lg bg-green-500/8 border border-green-500/20 px-3 py-2.5">
                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                <p className="text-sm text-green-500 leading-snug">{successMessage}</p>
              </div>
            )}

            {/* Remember me + Forgot password */}
            <div className="flex items-center justify-between pt-0.5">
              <label className="flex items-center gap-2 cursor-pointer group select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-border accent-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                />
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  Remember me
                </span>
              </label>

              {!isSignUp && (
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={isSubmitting}
                  className="text-sm text-primary hover:text-primary/75 transition-colors disabled:opacity-50"
                >
                  Forgot password?
                </button>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="relative w-full bg-primary text-primary-foreground rounded-lg py-2.5 text-sm font-semibold transition-all duration-200 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed mt-1"
            >
              <span className={`transition-opacity duration-200 ${isSubmitting ? 'opacity-0' : 'opacity-100'}`}>
                {isSignUp ? 'Create Account' : 'Sign In'}
              </span>
              {isSubmitting && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                </div>
              )}
            </button>
          </form>

          {/* Toggle mode */}
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              onClick={toggleMode}
              className="text-primary hover:text-primary/75 font-medium transition-colors"
            >
              {isSignUp ? 'Sign in' : 'Sign up'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
